import { ISliderValues } from '../types';
import Slider from '../../../components/Slider';
import React, { useState } from 'react';

interface IRangeWithLegendProps {
  min: number;
  max: number;
  legend: string;
  items: ISliderValues;
  position: number;
  setPosition: (position: number) => void;
  setHasAtLeastOneValueChanged?: (hasAtLeastOneValueChanged: boolean) => void;
}

const RangeWithLegend: React.FC<IRangeWithLegendProps> = ({
  min,
  max,
  legend,
  items,
  position,
  setPosition,
  setHasAtLeastOneValueChanged,
}) => {
  const [unreachedPosition, setUnreachedPosition] = useState(position);

  const updatePosition = (value: number) => {
    setHasAtLeastOneValueChanged?.(true);
    setPosition(value);
    setUnreachedPosition(value);
  };
  const updateInProgressPosition = (value: number) => {
    if (unreachedPosition !== value) {
      setUnreachedPosition(value);
    }
  };

  return (
    <>
      <div className="container">
        <div className="legend-container">
          <p>{legend}</p>
        </div>
        <div className="slider-and-positions-container">
          <div className="slider-container">
            <Slider
              min={min}
              max={max}
              values={[position]}
              snap={true}
              onChange={({ values }) => updatePosition(values[0])}
              onValuesUpdated={({ values }) =>
                updateInProgressPosition(values[0])
              }
              customBg={() => {
                return <div className="bg" />;
              }}
              customHandle={({ handleRef, ...props }) => {
                return <div className="handle" ref={handleRef} {...props} />;
              }}
              pitPoints={items.map((_, i) => i)}
              pitComponent={({ style, children }) => {
                return (
                  <div
                    style={{ ...style }}
                    className={`pit ${
                      children > unreachedPosition ? 'pit-unreached' : ''
                    }`}
                  >
                    <p className="pit-legend">{items[children]}</p>
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
      <style jsx>{`
        .container {
          --multislider-outline: 8px;
          --multislider-bar-height: 14px;
          --multislider-handle-width: 24px;
          --multislider-pit-width: 8px;
        }

        .legend-container {
          text-align: left;
          margin-bottom: var(--space-4);
        }

        .slider-container {
          margin: 0 var(--multislider-outline);
        }

        .bg {
          height: var(--multislider-bar-height);
          border-radius: var(--rd-2);
          background: var(--white);

          margin: 0 calc(-1 * var(--multislider-outline));
        }

        .slider-container :global(.handleContainer) {
          top: 0;
          left: 0;
          height: var(--multislider-bar-height);
        }

        .handle {
          width: var(--multislider-handle-width);
          height: var(--multislider-handle-width);
          margin-top: calc(
            (var(--multislider-bar-height) / 2) -
              (var(--multislider-handle-width) / 2)
          );
          margin-left: calc(-1 * (var(--multislider-handle-width) / 2));
          z-index: 1;
          background: var(--clr-tertiary-1);
          border: 3px solid var(--white);
          border-radius: var(--rd-round);
          cursor: pointer;
        }

        .slider-container :global(.DefaultProgressBar_background__horizontal) {
          margin: 0 calc(-1 * var(--multislider-outline));
          height: var(--multislider-bar-height);
          border-top-left-radius: var(--rd-2);
          border-bottom-left-radius: var(--rd-2);
        }

        .pit {
          min-width: 0;
          max-width: none;
        }
        .pit:before {
          content: '';
          position: absolute;
          top: calc(
            -1 * (var(--multislider-bar-height) / 2 +
                  var(--multislider-pit-width) / 2)
          );
          left: calc(-1 * (var(--multislider-pit-width) / 2));
          display: block;
          height: var(--multislider-pit-width);
          width: var(--multislider-pit-width);
          border-radius: var(--rd-round);
          background: var(--white);
        }

        .pit-unreached:before {
          background: var(--clr-tertiary-1);
        }

        .pit-legend {
          --multislider-legend-width: 80px;
          margin-top: var(--space-3);
          display: block;
          margin-left: calc(-1 * (var(--multislider-legend-width) / 2));
          width: var(--multislider-legend-width);
          text-align: center;
        }

        @media (max-width: 800px) {
          .pit-legend {
            --multislider-legend-width: 60px;
            font-size: var(--font-size-1);
          }
        }
      `}</style>
    </>
  );
};

export default RangeWithLegend;
